import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MiniCandleChart from './MiniCandleChart';
import './V1ChartApprovePanel.css';

/** Exactly 2 tabs under the AI chart — as per user sketch. Buy tab is below (TradingActionBoard). */
const TABS = [
  { id: 'approve', label: 'Approve stock & set priority' },
  { id: 'rejected', label: 'Rejected stocks' }
];

function fmtPx(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `₹${Number(n).toFixed(2)}`;
}

function reasonLabel(r) {
  const x = String(r || '').toLowerCase();
  if (x.includes('down')) return 'AI: DOWN';
  if (x.includes('sideways')) return 'AI: sideways';
  if (x.includes('volume')) return 'Volume weak';
  if (x.includes('net')) return 'Net too low';
  if (x.includes('rs')) return 'RS fail';
  if (x.includes('ai_approve') || x.includes('chart_pass') || x.includes('ai_long')) return 'AI LONG (up)';
  if (x.includes('ai_short')) return 'AI SHORT (down)';
  if (x.includes('ai_reject')) return 'AI rejected';
  if (x.includes('approved')) return 'Approved';
  if (x.includes('manual')) return 'Manual reject';
  if (x.includes('short')) return 'SHORT';
  if (x.includes('long')) return 'LONG';
  return r || '—';
}

export default function V1ChartApprovePanel({
  chartGate,
  onChanged,
  forcedTab = null,
  apiBase = '/api/normal-v1',
  candlesApi = '/api/normal/candles',
  priceMap = null,
  title = 'CNC → Chart AI test (FULL AUTO)',
  subtitle = 'Stocks from CNC delivery pipeline pass chart test here. AI checks UP or DOWN. Passed → Approve tab (priority set) → Buy tab below. Failed → Rejected tab.'
}) {
  const [tab, setTab] = useState('approve');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [priorityDraft, setPriorityDraft] = useState({});
  const [selectedStock, setSelectedStock] = useState(null);

  const activeTab = forcedTab === 'approve' || forcedTab === 'rejected' ? forcedTab : tab;
  const gateApi = String(apiBase || '/api/normal-v1').replace(/\/$/, '');

  const gate = chartGate || {
    awaitingApprove: [],
    approved: [],
    rejected: [],
    counts: { awaiting: 0, approved: 0, rejected: 0 },
    fullyAutomated: true
  };

  // Chart picker: AI-approved first, then awaiting, then rejected
  const chartCandidates = useMemo(() => {
    const map = new Map();
    for (const r of [...(gate.approved || []), ...(gate.awaitingApprove || []), ...(gate.rejected || [])]) {
      if (r?.stock && !map.has(r.stock)) map.set(r.stock, r);
    }
    return [...map.values()];
  }, [gate]);

  useEffect(() => {
    if (!chartCandidates.length) {
      setSelectedStock(null);
      return;
    }
    if (!selectedStock || !chartCandidates.some((r) => r.stock === selectedStock)) {
      setSelectedStock(chartCandidates[0].stock);
    }
  }, [chartCandidates, selectedStock]);

  const selectedRow = chartCandidates.find((r) => r.stock === selectedStock) || null;
  const selectedLivePx = (() => {
    if (!selectedRow?.stock) return null;
    const q = priceMap?.[selectedRow.stock] || priceMap?.[selectedRow.symbol];
    const fromMap = Number(q?.price ?? q);
    if (fromMap > 0) return fromMap;
    const fromRow = Number(selectedRow.currentPrice ?? selectedRow.ltp ?? selectedRow.price);
    return fromRow > 0 ? fromRow : null;
  })();

  // Approve tab = AI-approved list (auto → Buy). Rejected tab = AI rejects.
  const rows = useMemo(() => {
    if (activeTab === 'rejected') return gate.rejected || [];
    return gate.approved || [];
  }, [activeTab, gate]);

  const run = async (action, body = {}) => {
    const key = `${action}:${body.stock || 'all'}`;
    setBusy(key);
    setError(null);
    try {
      const res = await axios.post(`${gateApi}/chart-gate/${action}`, body, { timeout: 15000 });
      if (res.data?.ok === false) throw new Error(res.data.message || 'Failed');
      if (typeof onChanged === 'function') await onChanged(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Chart gate failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="v1-chart-gate card">
      <header className="v1-chart-gate-head">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <div className="v1-chart-gate-counts">
          <span>Approved {gate.counts?.approved ?? 0}</span>
          <span>Rejected {gate.counts?.rejected ?? 0}</span>
        </div>
      </header>

      {/* 1) Chart block — always on top (as in sketch) */}
      <div className="v1-chart-tab-layout">
        <aside className="v1-chart-tab-copy">
          <h4>Chart test (from CNC pipeline)</h4>
          <p>
            Stock comes from CNC delivery pipeline here to pass test based on chart.
            AI checks if this share goes up or down. If passed by AI → Approve tab.
            If failed by AI → Rejected tab. Approved shares only move to Buy tab below.
          </p>
          <label className="v1-chart-stock-pick">
            Select stock
            <select
              value={selectedStock || ''}
              onChange={(e) => setSelectedStock(e.target.value || null)}
            >
              {!chartCandidates.length && <option value="">No scan stocks yet</option>}
              {chartCandidates.map((r) => (
                <option key={r.stock} value={r.stock}>
                  {r.stock} · {fmtPx(r.price)} · {reasonLabel(r.reason)}
                </option>
              ))}
            </select>
          </label>
          {selectedRow && (
            <div className="v1-chart-ai-box">
              <span className={String(selectedRow.candleTrend || selectedRow.direction || 'UP').toLowerCase().includes('down') ? 'down' : 'up'}>
                AI chart: {(selectedRow.candleTrendLabel || selectedRow.direction || selectedRow.candleTrend || 'UP').toString().toUpperCase()}
                {selectedRow.candleTrendPct != null ? ` ${Number(selectedRow.candleTrendPct).toFixed(2)}%` : ''}
              </span>
              <strong className="v1-chart-ai-verdict">
                {selectedRow.side === 'short' || selectedRow.positionSide === 'short'
                  || String(selectedRow.reason || '').includes('ai_short')
                  || String(selectedRow.candleTrendLabel || '').toLowerCase().includes('short')
                  ? 'Decision: SHORT → Buy tab (earn if down)'
                  : (String(selectedRow.candleTrend || selectedRow.direction || '').toLowerCase().includes('up')
                    || String(selectedRow.reason || '').includes('ai_approve')
                    || String(selectedRow.reason || '').includes('ai_long')
                    || selectedRow.side === 'long'
                    || selectedRow.aiApproved
                    ? 'Decision: LONG → Buy tab (earn if up)'
                    : 'Decision: wait (sideways)')}
              </strong>
              <small>{reasonLabel(selectedRow.reason)}</small>
              {selectedRow.priority != null && (
                <small>Priority #{selectedRow.priority} → Buy tab</small>
              )}
            </div>
          )}
        </aside>
        <div className="v1-chart-tab-visual" aria-label="Candlestick chart">
          <div className="v1-chart-title-bar">
            <strong>{selectedRow ? `${selectedRow.stock} — Chart` : 'Chart Title'}</strong>
            <em>Open · High · Low · Close · 1m live</em>
          </div>
          {selectedRow ? (
            <MiniCandleChart item={selectedRow} livePrice={selectedLivePx} height={220} candlesApi={candlesApi} />
          ) : (
            <div className="v1-chart-placeholder">
              Waiting for CNC pipeline scan… Chart appears when stocks enter chart test.
            </div>
          )}
        </div>
      </div>

      {/* 2) Exactly two tabs under chart */}
      <div className="v1-chart-two-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            className={activeTab === t.id ? 'is-active' : ''}
            aria-selected={activeTab === t.id}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            <em>
              {t.id === 'approve' ? (gate.counts?.approved ?? 0) : (gate.counts?.rejected ?? 0)}
            </em>
          </button>
        ))}
      </div>

      <div className="v1-chart-to-buy-banner">
        Approved stocks only moved to Buy tab (below)
      </div>

      {error && <div className="v1-chart-gate-error">{error}</div>}

      <div className="v1-chart-gate-list">
        {rows.length === 0 ? (
          <p className="v1-chart-gate-empty">
            {activeTab === 'approve'
              ? 'No AI-approved stocks yet — after market open, CNC + chart AI fills this and sends them to Buy tab.'
              : 'No rejected stocks yet.'}
          </p>
        ) : (
          rows.map((row) => {
            const sk = row.stock;
            const draftPri = priorityDraft[sk] ?? row.priority ?? 1;
            const rowBusy = busy && String(busy).includes(sk);
            return (
              <article key={`${activeTab}-${sk}`} className="v1-chart-gate-row">
                <div className="v1-cg-main">
                  <button
                    type="button"
                    className="v1-cg-open-chart"
                    onClick={() => setSelectedStock(sk)}
                    title="Show on Chart"
                  >
                    Chart
                  </button>
                  <strong>{sk}</strong>
                  <span className="v1-cg-exch">{row.exchange || 'NSE'}</span>
                  <span>{fmtPx(row.price)}</span>
                  <span className={(row.direction || 'UP') === 'UP' ? 'up' : 'down'}>
                    {row.candleTrendLabel || row.direction || 'UP'}
                    {row.candleTrendPct != null ? ` ${Number(row.candleTrendPct).toFixed(2)}%` : ''}
                  </span>
                  {row.priority != null && <span className="v1-cg-pri">P{row.priority}</span>}
                  <small title={row.reason}>{reasonLabel(row.reason)}</small>
                </div>
                <div className="v1-cg-controls">
                  {activeTab === 'approve' && (
                    <>
                      <label>
                        Priority
                        <input
                          type="number"
                          min={1}
                          max={99}
                          value={draftPri}
                          onChange={(e) => setPriorityDraft((p) => ({ ...p, [sk]: Number(e.target.value) || 1 }))}
                        />
                      </label>
                      <button
                        type="button"
                        className="v1-cg-btn"
                        disabled={!!busy}
                        onClick={() => run('priority', { stock: sk, priority: draftPri })}
                      >
                        {rowBusy ? '…' : 'Set priority'}
                      </button>
                      <button
                        type="button"
                        className="v1-cg-btn danger"
                        disabled={!!busy}
                        onClick={() => run('reject', { stock: sk })}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {activeTab === 'rejected' && (
                    <button
                      type="button"
                      className="v1-cg-btn primary"
                      disabled={!!busy}
                      onClick={() => run('approve', { stock: sk, priority: draftPri || 9 })}
                    >
                      Re-approve → Buy
                    </button>
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
